import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowRightIcon,
  MapPinIcon,
  ArchiveBoxIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  ArchiveBoxArrowDownIcon,
  HomeIcon,
  ViewColumnsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import AccountInfoSection from "./accountInfoSection";
import AccountPasswordSection from "./accountPasswordSection";
import AccountAddressSection from "./accountAddressSection";
import CustomBigButton from "./customBigButton";
import CartChooseButton from "../cartAddress/customButtonChooseCart";
import ImportantAdver from "../controlAccount/importantAdver";
import BoxSection from "../controlAccount/boxSection";
import TitleAndIcon from "../controlAccount/titleAndIcon";
import BlockedItems from "../controlAccount/blockedItems";
import FinishedOrders from "../finishedOrders/finishedOrdersSection";
import CustomOrders from "../myOrders/CustomOrder";
import CustomBuyForMeCardList from "../ordersForBuyForMe/listofCardsOrdersBuyForMe";
import BoxChooseButton from "../boxDetials/customButtonBox";
import { useTranslation } from "react-i18next";
import { getAddressesByUserId } from "../../redux/reducer/userAddressReducer";
import { useLocation } from "react-router-dom";
import LoadingSpinner from "../animation/LoadingSpinner";
import BlockedItemsSlider from "../explainBuyPage/BlockedItemsSliderSection";
import { fetchMyCarts } from "../../redux/reducer/cartReducer";
import { getProductItemById } from "../../redux/reducer/productReducer";
import ItemsBuyMeSection from "../buyForMe/ItemsBuyMeSection/itemsSection";
import useLanguageDirection from "../../hooks/useLanguageDirection";

const SidebarUser = () => {
  const [selectedMenuItem, setSelectedMenuItem] =
    useState("AccountUserSection");
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Retrieve orders from the Redux store
  const { addresses, loading, error } = useSelector(
    (state) => state.userAddress
  );
  const location = useLocation(); // Use useLocation to get the current URL
  const { allProducts, productsLoading, productsError } = useSelector(
    (state) => state.products
  );
  const { carts, cartsLoading, cartsError } = useSelector(
    (state) => state.carts
  );
  const direction = useLanguageDirection();

  useEffect(() => {
    dispatch(getAddressesByUserId());
    if (addresses?.user?.role === "customer") {
      dispatch(fetchMyCarts());
      if (carts === 0) {
        dispatch(getProductItemById());
      }
    }
  }, [dispatch]);

  // Function to convert menu key to URL path
  const getUrlPathFromMenuKey = (menuKey) => {
    const pathMap = {
      ControlBoxSection: "dashboard",
      CartChooseButton: "box-address",
      ItemsBuyMeSection: "buy-for-me",
      Company: "my-orders",
      ListOfBoxCardOrders: "your-box-contents",
      FinishedOrders: "finished-orders",
      CustomOrders: "my-shipments",
      AccountUserSection: "",
    };
    return pathMap[menuKey] || "";
  };

  const handleMenuItemClick = (key) => {
    setSelectedMenuItem(key);
    setMenuOpen(false);

    const urlPath = getUrlPathFromMenuKey(key);

    const pathParts = window.location.pathname.split("/");
    const currentLang =
      pathParts[1] === "ar" || pathParts[1] === "en" ? pathParts[1] : "ar";

    const baseUrl = `/${currentLang}/my-account`;

    let newUrl;
    if (urlPath) {
      newUrl = `${baseUrl}/${urlPath}`;
    } else {
      newUrl = baseUrl;
    }

    window.history.replaceState(null, "", newUrl);
  };

  const boxNumber = addresses?.user?.boxNumber;

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    switch (currentPath) {
      case "dashboard":
        setSelectedMenuItem("ControlBoxSection");
        break;
      case "box-address":
        setSelectedMenuItem("CartChooseButton");
        break;
      case "buy-for-me":
        setSelectedMenuItem("ItemsBuyMeSection");
        break;
      case "my-orders":
        setSelectedMenuItem("Company");
        break;
      case "your-box-contents":
        setSelectedMenuItem("ListOfBoxCardOrders");
        break;
      case "finished-orders":
        setSelectedMenuItem("FinishedOrders");
        break;
      case "my-shipments":
        setSelectedMenuItem("CustomOrders");
        break;
      default:
        setSelectedMenuItem("AccountUserSection");
    }
  }, [location]);

  if (error) {
    return (
      <p className="text-red-500">
        {t("error_occurred")}: {error}
      </p>
    );
  }

  // buy-for-me
  const pendingProducts =
    allProducts?.filter((product) => product.status === "pending") || [];
  const pendingCount = pendingProducts.length;

  const pendingOrPricedCarts =
    carts?.filter(
      (cart) => cart.status === "pending" || cart.status === "priced"
    ) || [];

  const Buy_For_Me_Count = pendingOrPricedCarts.length;

  // my-orders
  const purchasedOrPricedCarts =
    carts?.filter(
      (cart) => cart.status === "purchased" || cart.status === "requested"
    ) || [];

  const My_Orders_Count = purchasedOrPricedCarts.length;

  // your-box-contents
  const receivedOrPricedCarts =
    carts?.filter((cart) => cart.status === "received") || [];

  const Your_Box_Contents_Count = receivedOrPricedCarts.length;

  // my-shipments
  const packedOrPricedCarts =
    carts?.filter(
      (cart) =>
        cart.status === "packed" ||
        cart.status === "shipped" ||
        cart.status === "delivered"
    ) || [];

  const My_Shipments_Count = packedOrPricedCarts.length;

  // finished-orders
  const expiredOrPricedCarts =
    carts?.filter((cart) => cart.status === "expired") || [];

  const Finished_Orders_Count = expiredOrPricedCarts.length;
  // console.log(addresses);
  return (
    <div className="max-container" dir={direction}>
      <div className="flex-col px-4 lg:px-0 md:flex min-h-[70vh] mb-4 ">
        <div className="w-full px-0 md:flex lg:px-0 md:px-2">
          {/* Sidebar Section */}
          <div className="md:w-1/4 ">
            <div className="hidden md:block">
              <div className="flex items-center justify-around w-full">
                <div className="w-1 h-20 mx-1 bg-black"></div>
                <div className="w-1 h-20 mx-1 bg-black"></div>
              </div>
            </div>

            {/* Hamburger Icon for Mobile */}
            <div className="flex items-center justify-between py-4 md:px-4 md:hidden">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="text-black"
              >
                {direction === "rtl" ? (
                  <ArrowRightIcon className="w-6 h-6 rotate-180 " />
                ) : (
                  <ArrowRightIcon className="w-6 h-6 " />
                )}
              </button>
            </div>

            {/* Drawer Menu for Mobile */}
            <div
              className={`${
                !menuOpen ? "opacity-0" : "opacity-100"
              } fixed top-0 ${
                direction === "rtl" ? "right-0" : "left-0"
              } z-10 w-[270px] h-full mt-[57px] bg-white shadow-lg transform transition-all duration-300 ease-in-out ${
                menuOpen
                  ? " translate-x-0 "
                  : direction === "rtl"
                  ? "translate-x-full "
                  : "-translate-x-full  "
              } md:hidden`}
            >
              <ul className="px-4 mt-2 space-y-2">
                <li className="group">
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="p-2 text-black"
                  >
                    {direction === "ltr" ? (
                      <ArrowRightIcon className="w-6 h-6 rotate-180" />
                    ) : (
                      <ArrowRightIcon className="w-6 h-6" />
                    )}
                  </button>
                </li>
                {[
                  {
                    label: t("AccountUserSection"),
                    icon: HomeIcon,
                    key: "AccountUserSection",
                  },
                  {
                    label: t("ControlBoxSection"),
                    icon: ViewColumnsIcon,
                    key: "ControlBoxSection",
                  },
                  {
                    label: t("CartChooseButton"),
                    icon: MapPinIcon,
                    key: "CartChooseButton",
                  },
                  {
                    label: t("ItemsBuyMeSection"),
                    icon: ShoppingCartIcon,
                    key: "ItemsBuyMeSection",
                  },
                  {
                    label: t("Company"),
                    icon: ClipboardDocumentListIcon,
                    key: "Company",
                  },
                  {
                    label: t("ListOfBoxCardOrders"),
                    icon: ArchiveBoxIcon,
                    key: "ListOfBoxCardOrders",
                  },
                  {
                    label: t("CustomOrders"),
                    icon: ArchiveBoxArrowDownIcon,
                    key: "CustomOrders",
                  },
                  {
                    label: t("FinishedOrders"),
                    icon: XMarkIcon,
                    key: "FinishedOrders",
                  },
                ].map((item) => (
                  <li
                    key={item.key}
                    className={`flex relative items-center px-4 py-2 text-black hover:bg-custom-gradient-div cursor-pointer ${
                      selectedMenuItem === item.key ? "bg-custom-yellow" : ""
                    }`}
                    onClick={() => handleMenuItemClick(item.key)}
                  >
                    <item.icon className="w-5 h-5 me-2" />
                    {item.label}
                    {/* Notification Badge */}
                    {item.key === "ItemsBuyMeSection" &&
                    Buy_For_Me_Count > 0 ? (
                      <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                        {Buy_For_Me_Count}
                      </span>
                    ) : (
                      item.key === "ItemsBuyMeSection" &&
                      pendingCount > 0 && (
                        <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                          {pendingCount}
                        </span>
                      )
                    )}
                    {item.key === "Company" && My_Orders_Count > 0 && (
                      <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                        {My_Orders_Count}
                      </span>
                    )}
                    {item.key === "ListOfBoxCardOrders" &&
                      Your_Box_Contents_Count > 0 && (
                        <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                          {Your_Box_Contents_Count}
                        </span>
                      )}
                    {item.key === "CustomOrders" && My_Shipments_Count > 0 && (
                      <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                        {My_Shipments_Count}
                      </span>
                    )}
                    {item.key === "FinishedOrders" &&
                      Finished_Orders_Count > 0 && (
                        <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                          {Finished_Orders_Count}
                        </span>
                      )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Menu for Desktop */}
            <div className="hidden md:block">
              <div className="flex flex-col w-full p-2 bg-white rounded-md shadow-md h-fit ">
                <ul className="space-y-2">
                  {[
                    {
                      label: t("AccountUserSection"),
                      icon: HomeIcon,
                      key: "AccountUserSection",
                    },
                    {
                      label: t("ControlBoxSection"),
                      icon: ViewColumnsIcon,
                      key: "ControlBoxSection",
                    },
                    {
                      label: t("CartChooseButton"),
                      icon: MapPinIcon,
                      key: "CartChooseButton",
                    },
                    {
                      label: t("ItemsBuyMeSection"),
                      icon: ShoppingCartIcon,
                      key: "ItemsBuyMeSection",
                    },
                    {
                      label: t("Company"),
                      icon: ClipboardDocumentListIcon,
                      key: "Company",
                    },
                    {
                      label: t("ListOfBoxCardOrders"),
                      icon: ArchiveBoxIcon,
                      key: "ListOfBoxCardOrders",
                    },
                    {
                      label: t("CustomOrders"),
                      icon: ArchiveBoxArrowDownIcon,
                      key: "CustomOrders",
                    },
                    {
                      label: t("FinishedOrders"),
                      icon: XMarkIcon,
                      key: "FinishedOrders",
                    },
                  ].map((item) => (
                    <li
                      key={item.key}
                      className={`flex relative items-center px-4 py-2 text-black hover:bg-custom-gradient-div cursor-pointer ${
                        selectedMenuItem === item.key ? "bg-custom-yellow" : ""
                      }`}
                      onClick={() => handleMenuItemClick(item.key)}
                    >
                      <item.icon className="w-5 h-5 me-2" />
                      {item.label}
                      {/* Notification Badge */}
                      {item.key === "ItemsBuyMeSection" &&
                      Buy_For_Me_Count > 0 ? (
                        <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                          {Buy_For_Me_Count}
                        </span>
                      ) : (
                        item.key === "ItemsBuyMeSection" &&
                        pendingCount > 0 && (
                          <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                            {pendingCount}
                          </span>
                        )
                      )}
                      {item.key === "Company" && My_Orders_Count > 0 && (
                        <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                          {My_Orders_Count}
                        </span>
                      )}
                      {item.key === "ListOfBoxCardOrders" &&
                        Your_Box_Contents_Count > 0 && (
                          <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                            {Your_Box_Contents_Count}
                          </span>
                        )}
                      {item.key === "CustomOrders" &&
                        My_Shipments_Count > 0 && (
                          <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                            {My_Shipments_Count}
                          </span>
                        )}
                      {item.key === "FinishedOrders" &&
                        Finished_Orders_Count > 0 && (
                          <span className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full end-6 top-1/2">
                            {Finished_Orders_Count}
                          </span>
                        )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {/* Content Section */}
          <div className="md:w-3/4 ">
            {selectedMenuItem === "AccountUserSection" && (
              <div className="flex flex-col justify-between  min-h-[500px] md:px-6 md:mt-20">
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <AccountInfoSection addresses={addresses?.user} />
                    {!addresses?.user?.isWithGoogle && (
                      <AccountPasswordSection />
                    )}
                    <AccountAddressSection addresses={addresses?.user} />
                    <CustomBigButton
                      addresses={addresses?.user?.addresses}
                      userId={addresses?.user?._id}
                    />
                  </>
                )}
                {/* <PaymentForm /> */}
              </div>
            )}
            {selectedMenuItem === "ControlBoxSection" && (
              <div className="md:px-6 md:mt-20">
                <TitleAndIcon />
                <BoxSection boxNumber={boxNumber || t("Unknown")} />
                <ImportantAdver />
                <span className="hidden md:block">
                  <BlockedItems />
                </span>
                <span className="block md:hidden">
                  <BlockedItemsSlider header={false} />
                </span>
              </div>
            )}
            {selectedMenuItem === "CartChooseButton" && (
              <div className=" md:px-6 md:mt-20">
                <CartChooseButton
                  boxNumber={addresses?.user?.boxNumber || t("Unknown")}
                />
              </div>
            )}
            {selectedMenuItem === "ListOfBoxCardOrders" && (
              <div className=" md:px-6 md:mt-20">
                <BoxChooseButton />
              </div>
            )}
            {selectedMenuItem === "ItemsBuyMeSection" && (
              <div className=" md:px-6 md:mt-20">
                <ItemsBuyMeSection />
              </div>
            )}
            {selectedMenuItem === "Company" && (
              <div className=" md:px-6 md:mt-20">
                <CustomBuyForMeCardList />
              </div>
            )}
            {selectedMenuItem === "FinishedOrders" && (
              <div className=" md:px-6 md:mt-20">
                <FinishedOrders />
              </div>
            )}
            {selectedMenuItem === "CustomOrders" && (
              <div className=" md:px-6 md:mt-20">
                <CustomOrders />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidebarUser;
